<template>
  <HeaderComponent />
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">{{ errorMessage }}</div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">{{ successMessage }}</div>
  </div>

  <section class="w-full min-h-screen pt-[120px] pb-[60px] bg-gray-100 flex justify-center items-start">
    <div class="container lg:max-w-[1200px] px-[20px] flex justify-center w-full">
      <div class="w-full max-w-[420px] bg-white rounded-[10px] shadow-4xl overflow-hidden">
        <div class="w-full bg-orange-600 p-[25px] flex flex-col items-center">
          <img src="../assets/logo.png" alt="" class="w-[50px] h-[50px] mb-[10px]">
          <h1 class="text-white text-xl font-bold">Créer un compte</h1>
          <p class="text-orange-100 text-xs mt-[5px]">Payez votre téléphone par tranches</p>
        </div>
        <form @submit.prevent="register" class="w-full p-[25px]">
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Nom complet</label>
            <input v-model="nom" type="text" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Votre nom">
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Numéro WhatsApp</label>
            <input v-model="whatsapp" type="tel" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Ex: 0166519432">
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Email <span class="text-gray-400 font-normal">(optionnel — pour être notifié)</span></label>
            <input v-model="email" type="email" class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="votre@email.com">
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Mot de passe</label>
            <div class="relative">
              <input v-model="password" :type="showPassword ? 'text' : 'password'" required class="w-full p-[12px] pr-[42px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Choisissez un mot de passe">
              <button type="button" @click="showPassword = !showPassword" class="absolute right-[12px] top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i :class="showPassword ? 'fa-eye-slash' : 'fa-eye'" class="fa-solid"></i>
              </button>
            </div>
          </div>
          <div class="w-full my-[10px]">
            <label class="text-sm font-bold text-gray-700 mb-[5px] block">Confirmer le mot de passe</label>
            <div class="relative">
              <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" required class="w-full p-[12px] pr-[42px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Retapez le mot de passe">
              <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute right-[12px] top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i :class="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'" class="fa-solid"></i>
              </button>
            </div>
            <p v-if="confirmPassword && password !== confirmPassword" class="text-xs text-red-500 mt-[5px]">Les mots de passe ne correspondent pas.</p>
          </div>
          <button type="submit" :disabled="isLoading" class="w-full h-[48px] mt-[15px] flex justify-center items-center p-[10px] text-sm font-bold rounded-[5px] bg-orange-600 hover:bg-orange-700 text-gray-100 transition-colors">
            <span v-if="!isLoading">S'inscrire</span>
            <div v-else class="spinner1"><div></div><div></div><div></div><div></div><div></div></div>
          </button>
          <p class="text-center text-sm text-gray-500 mt-[20px]">
            Déjà un compte ?
            <router-link to="/connexion" class="text-orange-600 font-bold hover:underline">Connectez-vous</router-link>
          </p>
        </form>
      </div>
    </div>
  </section>
  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { useAuthClientStore } from '@/store/useAuthClientStore'

const router = useRouter()
const route = useRoute()
const authClientStore = useAuthClientStore()

const nom = ref('')
const whatsapp = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const register = async () => {
  if (password.value !== confirmPassword.value) {
    errorMessage.value = 'Les mots de passe ne correspondent pas.'
    setTimeout(() => (errorMessage.value = ''), 4000)
    return
  }
  isLoading.value = true
  try {
    const { data } = await axios.post('/api/clients/register', {
      nom: nom.value,
      whatsapp: whatsapp.value,
      email: email.value || undefined,
      password: password.value,
    })
    authClientStore.setAuth(data.token, data.client)
    successMessage.value = 'Compte créé avec succès !'
    const redirect = route.query.redirect || '/mon-espace'
    setTimeout(() => router.push(redirect), 500)
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Erreur lors de l\'inscription.'
    setTimeout(() => (errorMessage.value = ''), 4000)
  } finally {
    isLoading.value = false
  }
}
</script>
